import instance from '../xhrService';
import db from '../../db/db';
import { random } from 'lodash';
import { withOfflineFallback, withOfflineWrite } from '../offlineFallback';
import { syncAfterOnlineSave } from '../offlineSyncSend';
import {
  isLegendItemType,
  toCanonicalPositionableType,
} from '../../components/MapEditor/Positionable/legendItemType';

const baseUrl = '/api/positionables';
const url = (a, b, c, d) =>
  `${baseUrl}/${a}` +
  (b ? `/${b}` : '') +
  (c ? `/${c}` : '') +
  (d ? `/${d}` : '');

async function updateOffline(tType, id, data, online, replay = false) {
  const pidArray = [];

  for (let i = 0; i < data.length; i++) {
    const pId = data[i].id ? data[i].id : random(false, 1000000);

    // `inspection` is only needed for project_id. Requiring it here silently
    // dropped the whole save (no write, no error) whenever the lookup missed —
    // which it does for a positionable created in an earlier inspection, since
    // the download only stores the current one.
    const inspection = await db.inspections.get(data[i].start_inspection_id);

    let projectId = inspection?.project_id;

    if (!projectId && data[i].id) {
      const existing = await db.positionables.get(data[i].id);

      projectId = existing?.project_id;
    }

    if (!projectId) {
      // Online the offline row is only a mirror of what the server already
      // holds, so skipping what we cannot place loses nothing. Offline it is the
      // only copy, so dropping it silently is not acceptable.
      //
      // There is deliberately no "if only one project is downloaded, assume it"
      // guess here. It attributed the record to whichever project happened to be
      // downloaded rather than the one being edited, and wrote it
      // `{ synced: true }` so it was never reconciled — foreign positionables
      // then rendered on the wrong project's offline map.
      if (online) {
        continue;
      }

      throw new Error(
        'Cannot save positionable offline: project could not be determined',
      );
    }

    const edit = {
      method: 'update',
      target_id: id,
      tType: tType,
      ...data[i],
    };

    const syncObj = {
      synced: false,
      payload: [edit],
    };

    if (online) {
      syncObj.synced = true;
      syncObj.payload = [];
    }

    const positionableObj = {
      ...data[i],
      id: pId,
      tType: tType,
      // SW2-2230. The map editor hands us whichever spelling its data source uses
      // ("control_measure" / "pollutant"), and this row is the same primary key the offline
      // download wrote as "legend_items" — so without normalising, saving a map edit rewrites the
      // cached row under a spelling the finding picker does not recognise, and the placement
      // silently disappears from the picker offline.
      positionable_type: toCanonicalPositionableType(data[i].positionable_type),
      project_id: projectId,
      target_id: Number(id),
      sync: {
        ...syncObj,
      },
    };

    if (data[i].id) {
      const existingPositionable = await db.positionables.get(pId);

      if (existingPositionable) {
        // A downloaded positionable is stored { synced: true, payload: [] }.
        // Reusing that object for an offline edit kept synced === true, so the
        // record carried a queued payload the sync query (!sync.synced) skipped
        // forever — which is why new positionables synced but edits never did.
        // The online branch also pushed payload[0] (undefined, since payload is
        // empty when online), appending junk to the queue.
        const previousPayload = existingPositionable.sync?.payload || [];

        // SW2-2446: a replay is the sync sending this row's own queue. The row is left as it is:
        // the sync takes off the entries it sent, and the row's fields are at least as new as the
        // server's answer (a move saved while the request was in flight would be overwritten by
        // the older position the server returns).
        if (!replay) {
          // SW2-2446: an online save keeps any placement edit queued offline before it, and is
          // queued behind it so the sync does not replay the older position over this one.
          const mergedSync = online
            ? syncAfterOnlineSave(existingPositionable.sync, edit)
            : {
                synced: false,
                payload: [...previousPayload, ...positionableObj.sync.payload],
              };

          await db.positionables.update(pId, {
            ...positionableObj,
            sync: mergedSync,
          });
        }
      } else {
        await db.positionables.put(positionableObj).catch((e) => {
          console.error('Error saving positionable to db', e);
        });
      }
    } else {
      await db.positionables.put(positionableObj).catch((e) => {
        console.error('Error saving positionable to db', e);
      });
    }
    pidArray.push(pId);
  }

  // Nothing mirrored while online is the normal case for a project the user has
  // not downloaded, so it must not be an error. Offline, an empty result means
  // the edit was not stored anywhere and the caller has to hear about it.
  if (!pidArray.length) {
    if (online) {
      return [];
    }

    throw new Error('Positionable not found in offline db');
  }

  const positonables = await db.positionables
    .where('id')
    .anyOf(pidArray)
    .toArray();

  if (positonables.length === 0 && !online) {
    throw new Error('Positionable not found in offline db');
  }

  return positonables;
}

/**
 * SW2-2230 — offline source for the finding form's "specific legend item on the map" picker.
 *
 * Deliberately does NOT reuse `positionableApi.index`'s `fromCache`: that one filters on
 * `target_id` / `project_id` / `start_inspection_id` only. It ignores the legend subtype, the
 * `end_inspection_id` window and `marked_deleted`, and stamps `positionable_type` onto whatever it
 * matched — so a picker built on it would offer removed placements and finding markers.
 *
 * Returns `null` (not `[]`) when the inspection is not in the cache, so a genuine miss re-raises the
 * request error instead of silently rendering an empty picker.
 */
const legendPlacementsFromCache = async (inspectionId) => {
  const iId = Number(inspectionId);
  const inspection = await db.inspections.get(iId);

  if (!inspection) {
    return null;
  }

  // Read the junction table directly. `uploadApi.projectInspection.index` still uses a bare
  // `navigator.onLine` pre-check, which reports true after an offline reload and then throws.
  const uploadsRow = await db.projectInspectionUploads
    .where('project_id')
    .equals(Number(inspection.project_id))
    .and((row) => row.inspection_id === iId)
    .first();

  const maps = uploadsRow?.data || [];

  if (!maps.length) {
    return [];
  }

  const mapNameById = {};
  const projectUploadIds = maps.map((upload) => {
    mapNameById[upload.project_upload_id] =
      upload.name || upload.project_upload?.name || null;

    return upload.project_upload_id;
  });

  const rows = await db.positionables
    // target_id is indexed; everything after is an in-memory filter over a handful of maps.
    .where('target_id')
    .anyOf(projectUploadIds)
    .filter(
      (p) =>
        p.project_id === inspection.project_id &&
        // Mandatory: target_id is not unique across target types — finding markers point at
        // project_inspection_uploads ids, legend placements at project_uploads ids, and the two
        // id spaces can collide.
        //
        // Accepts every spelling rather than just the server's: rows written before the
        // normalisation in `updateOffline` landed still carry a map-editor subtype, and this is a
        // read of a cache we do not control the age of. See legendItemType.js.
        isLegendItemType(p.positionable_type) &&
        (p.start_inspection_id == null || p.start_inspection_id <= iId) &&
        (p.end_inspection_id == null || p.end_inspection_id > iId) &&
        !p.marked_deleted &&
        // Placements created offline carry a temp id from `random(false, 1000000)`, and findings
        // sync before positionables with no id remap — assigning one would dangle after sync.
        p.sync?.synced !== false,
    )
    .toArray();

  const legendItems = await db.legendItems
    .where('project_id')
    .equals(Number(inspection.project_id))
    .toArray();

  const nameById = {};
  const typeById = {};

  legendItems.forEach((item) => {
    nameById[item.id] = item.name;
    typeById[item.id] = item.type;
  });

  // Collapse each edit chain to one entry, keyed on the head, keeping the newest member that is
  // still at or before this inspection. Mirrors the server's DISTINCT ON.
  const byHead = new Map();

  rows.forEach((p) => {
    // `positionable_id` is the legend item FK, not a row id — the naming is a trap.
    if (!nameById[p.positionable_id]) {
      return;
    }

    const headId = p.source_positionable_id ?? p.id;
    const prev = byHead.get(headId);

    if (!prev || (p.start_inspection_id ?? 0) > (prev.start_inspection_id ?? 0)) {
      byHead.set(headId, p);
    }
  });

  return [...byHead.entries()]
    .map(([headId, p]) => ({
      id: headId,
      live_positionable_id: p.id,
      legend_item_id: p.positionable_id,
      legend_item_name: nameById[p.positionable_id],
      legend_item_type: typeById[p.positionable_id] || null,
      number: p.number ?? null,
      map_name: mapNameById[p.target_id] || null,
      label:
        p.number === null || p.number === undefined
          ? nameById[p.positionable_id]
          : `${nameById[p.positionable_id]} ${p.number}`,
      project_upload_id: p.target_id,
    }))
    .sort(
      (a, b) =>
        a.legend_item_name.localeCompare(b.legend_item_name) ||
        (a.number ?? 0) - (b.number ?? 0) ||
        a.id - b.id,
    );
};

export const positionableApi = {
  /**
   * SW2-2230. Legend items currently placed on the site maps attached to this inspection.
   * Returns `[]` when the sub-feature is off or the inspection has no placements — the finding
   * form then falls back to the legend-item-name field.
   */
  legendPlacementsByInspection: async (inspectionId) =>
    withOfflineFallback(
      () =>
        instance
          .get(`/api/inspections/${inspectionId}/legend-item-placements`)
          .then((response) => response.data),
      () => legendPlacementsFromCache(inspectionId),
      // Same reasoning as `index` below: a cache hit is an array and `[]` is truthy, so a 500 on a
      // downloaded project would otherwise render an empty picker with no error, which is
      // indistinguishable from "this inspection has no placements".
      { onlyWhenUnreachable: true },
    ),

  mapVersions: (projectId, projectUploadId) =>
    instance
      .get(`${baseUrl}/map-versions/${projectId}/${projectUploadId}`)
      .then((response) => response.data),

  index: async (
    tType,
    tId,
    iId,
    pType,
    pSubType = null,
    showRemoved = false,
    versionDate = null,
  ) => {
    const params = {};

    if (pSubType) {
      params.type = pSubType;
    }
    params.showRemoved = showRemoved;

    if (versionDate) {
      params.versionDate = versionDate;
    }
    const supportsOffline =
      tType === 'project_inspection_uploads' || tType === 'project_uploads';

    const fromCache = async () => {
      if (!supportsOffline) {
        return null;
      }

      const inspection = await db.inspections.get(iId);

      if (!inspection) {
        return null;
      }

      const data = await db.positionables
        .where('target_id')
        .equals(tId)
        .and((item) => item.project_id === inspection.project_id)
        .and((item) => item.start_inspection_id <= iId)
        .toArray();

      return data.map((item) => ({ ...item, positionable_type: pType }));
    };

    return withOfflineFallback(
      () =>
        instance
          .get(url(tType, tId, iId, pType), { params })
          .then((response) => response.data),
      fromCache,
      // A cache hit here is an array, and `[]` is truthy, so a 500 on a
      // downloaded project would otherwise render a map with zero positionables
      // and no error — an inspector could redraw features that already exist.
      // Restricting the fallback to genuine unreachability keeps the offline
      // path working (a reload while offline still fails network-shaped) while
      // letting real server errors surface.
      { onlyWhenUnreachable: true },
    );
  },
  // update or insert en-masse
  // SW2-2443: `opts.replay` must be passed when this is the offline sync
  // flushing a queued edit. Without it an unreachable server queued the edit
  // again and resolved, and the sync marked it synced having never sent it.
  bulkUpdate: async (tType, id, data, opts = {}) => {
    const supportsOffline =
      tType === 'project_inspection_uploads' || tType === 'project_uploads';

    // navigator.onLine reports true after reloading while offline, so this
    // previously took the online branch, failed the PUT, and never reached
    // updateOffline — silently losing the move/edit.
    if (!supportsOffline) {
      if (!navigator.onLine) {
        return Promise.reject(new Error('Offline mode not supported'));
      }

      return instance
        .put(url(tType, id, 'update'), data)
        .then((response) => response.data);
    }

    return withOfflineWrite(
      async () => {
        const result = await instance
          .put(url(tType, id, 'update'), data)
          .then((response) => response.data);

        // Best-effort mirror. The server is already authoritative by this point,
        // so a cache write failure must not fail the save — and must never reach
        // withOfflineWrite, which would queue a write the server has applied and
        // replay it on the next sync.
        try {
          await updateOffline(tType, id, result, true, opts.replay);
        } catch (err) {
          console.error(
            'Failed to mirror positionables to the offline cache',
            err,
          );
        }

        return result;
      },
      () => updateOffline(tType, id, data, false),
      { replay: opts.replay },
    );
  },
  bulkDelete(positionableIds, selectedInspId, versionDate) {
    /** @todo refactor to single xhr */
    return Promise.all(
      positionableIds.map((id) => this.delete(id, selectedInspId, versionDate)),
    ).then(() => null);
  },
  /**
   * SW2-2346 — versionDate is the map version the removal was made on. Sent only when
   * the user is not on the newest version; without it the backend stamps today and the
   * removal spawns a new version instead of recording against the one on screen.
   */
  delete: (id, selectedInspId, versionDate) => {
    const intId = parseInt(id);

    return instance
      .delete(url(intId) + `/${selectedInspId}`, {
        params: versionDate ? { versionDate } : undefined,
      })
      .then((response) => response);
  },
  editCreatedDate: (id, date) => {
    return instance
      .put(`${baseUrl}/${id}`, { updatedDate: date })
      .then((res) => res);
  },
  editRemovedOnDate: (id, date) => {
    return instance
      .put(`${baseUrl}/changeRemovedOnDate/${id}`, { updatedDate: date })
      .then((res) => res);
  },
  getPositionablesAuthor: (positionableId) => {
    return instance.get(`${baseUrl}/${positionableId}`).then((res) => res);
  },
  calibrateMapChanges: (data) => {
    return instance
      .post(`${baseUrl}/calibrateMapChanges`, data)
      .then((res) => res);
  },
};

export default positionableApi;
